// useMediaView — persisted "grid" vs "list" preference for media list screens.
// Source of truth: localStorage.userSettings.mediaView (alongside theme / language).
// Updates broadcast via a window event so multiple mounted views stay in sync.

import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "userSettings";
const EVENT_NAME = "mediaView:change";
const VALID_VIEWS = ["grid", "list"];
const DEFAULT_VIEW = "grid";

const readView = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_VIEW;
    const v = JSON.parse(raw)?.mediaView;
    return VALID_VIEWS.includes(v) ? v : DEFAULT_VIEW;
  } catch {
    return DEFAULT_VIEW;
  }
};

const writeView = (view) => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    const next = { ...(parsed && typeof parsed === "object" ? parsed : {}), mediaView: view };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
};

export default function useMediaView() {
  const [view, setViewState] = useState(readView);

  useEffect(() => {
    const onChange = (e) => {
      const next = e?.detail?.view;
      if (VALID_VIEWS.includes(next)) setViewState(next);
    };
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setViewState(readView());
    };
    window.addEventListener(EVENT_NAME, onChange);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EVENT_NAME, onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const setView = useCallback((next) => {
    if (!VALID_VIEWS.includes(next)) return;
    writeView(next);
    setViewState(next);
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { view: next } }));
  }, []);

  return [view, setView];
}
